import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Alert, Checkbox, Typography } from "antd";
import { deriveBookingMeta, describeCancelEffect } from "../lib/bookingMeta";
import { canForceCancel } from "../../../utils/roles";
import { cancelBooking } from "../../../services/booking-service";

const { TextArea } = Input;
const { Text } = Typography;

const formatAmount = (amount, currency) => {
  if (!currency) return "—";
  try {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const CancelBookingModal = ({
  open,
  booking,
  admin,
  retry = false,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [surname, setSurname] = useState("");
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastError, setLastError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setConfirmText("");
      setSurname("");
      setForce(false);
      setLastError(null);
    }
  }, [open, booking?._id]);

  if (!booking) return null;

  const meta = deriveBookingMeta(booking);
  const effectDescription = describeCancelEffect(booking);
  const requiresTypedPnr = meta.ticketed && meta.hasProviderRef;
  const showForceOption =
    canForceCancel(admin) &&
    (lastError?.code === "NO_PROVIDER_REFERENCE" ||
      lastError?.code === "NO_PROVIDER_INTEGRATION");

  const reasonValid = reason.trim().length >= 5 && reason.trim().length <= 500;
  const pnrConfirmed = !requiresTypedPnr || confirmText === meta.providerReference;
  const canSubmit = reasonValid && pnrConfirmed && !submitting;

  const handleSubmit = () => {
    setSubmitting(true);
    setLastError(null);

    const payload = {
      reason: reason.trim(),
      retry,
      force: force || undefined,
      providerOverrides:
        meta.provider === "TRIPS" && surname ? { surname } : undefined,
    };

    cancelBooking(booking._id, payload)
      .then(() => {
        setSubmitting(false);
        onSuccess?.();
      })
      .catch((err) => {
        setSubmitting(false);
        const body = err?.response?.data;
        setLastError(body ?? { error: "Something went wrong" });
      });
  };

  return (
    <Modal
      title={`Cancel booking ${booking.tramangoId}`}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Cancel booking"
      okButtonProps={{ danger: true, disabled: !canSubmit, loading: submitting }}
    >
      <p>
        <b>Customer:</b> {booking.firstName} {booking.lastName} ({booking.email})
      </p>
      <p>
        <b>Amount:</b> {formatAmount(booking.amount, booking.currency)}
      </p>
      {meta.hasProviderRef && (
        <p>
          <b>{meta.provider === "AMADEUS" ? "Order" : "PNR"}:</b>{" "}
          {meta.providerReference}
        </p>
      )}

      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="What will happen"
        description={effectDescription}
      />

      {meta.isPaid && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message="This booking is PAID"
          description="No refund is issued automatically. Finance must refund manually."
        />
      )}

      <Form layout="vertical">
        <Form.Item
          label="Cancellation reason (internal only — not shown to the customer)"
          required
        >
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="e.g. Customer requested cancellation via support ticket #1234"
          />
        </Form.Item>

        {requiresTypedPnr && (
          <Form.Item
            label={`Type the PNR (${meta.providerReference}) to confirm`}
            required
          >
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </Form.Item>
        )}

        {meta.provider === "TRIPS" && (
          <Form.Item label="Surname (required by Trips)">
            <Input
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              placeholder={booking.lastName}
            />
          </Form.Item>
        )}

        {showForceOption && (
          <Form.Item>
            <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)}>
              Force mark cancelled (no vendor call)
            </Checkbox>
          </Form.Item>
        )}
      </Form>

      {lastError && (
        <Alert
          type="error"
          showIcon
          message={lastError.error || "Cancellation failed"}
          description={
            lastError.providerErrors?.length ? (
              <ul>
                {lastError.providerErrors.map((e, i) => (
                  <li key={i}>{e.detail || JSON.stringify(e)}</li>
                ))}
              </ul>
            ) : lastError.code === "NO_PROVIDER_REFERENCE" ||
              lastError.code === "NO_PROVIDER_INTEGRATION" ? (
              <Text>
                {canForceCancel(admin)
                  ? "Check the force-cancel box above and submit again."
                  : "A SUPER admin is required to force-cancel this booking."}
              </Text>
            ) : null
          }
        />
      )}
    </Modal>
  );
};

export default CancelBookingModal;
